import { CalendarClock, Clock3, Star, TrendingUp, Wallet } from "lucide-react";

import { useMemo, useState } from "react";
import { Link } from "react-router";
import type { DashboardRange } from "@flexv-coach/shared";
import { Badge } from "@/components/ui/Badge";
import { Card, CardContent, CardHeader, StatsCard } from "@/components/ui/Card";
import { SegmentedControl } from "@/components/ui/SegmentedControl";
import { EmptyState, ErrorState, LoadingBlock } from "@/components/ui/States";
import { BreakdownDonutChart, type ChartSlice } from "@/components/charts/BreakdownDonutChart";
import { TrendBarChart, type TrendPoint } from "@/components/charts/TrendBarChart";
import { formatDayMonth, formatDuration, formatTimeRange, formatWeekday } from "@/lib/date";
import { statusLabel, statusTone } from "@/lib/labels";
import { ApiError } from "@/lib/api";
import { useAuth } from "@/providers/AuthProvider";
import { useI18n } from "@/hooks/useI18n";
import { useCoachDashboard } from "./useCoachDashboard";

/** Warna status kehadiran — hijau untuk selesai, merah untuk batal/absen. */
const ATTENDANCE_COLORS: Record<string, string> = {
    completed: "var(--color-tertiary)",
    confirmed: "#2563eb",
    scheduled: "var(--color-primary)",
    ongoing: "var(--color-secondary)",
    cancelled: "var(--color-error)",
    no_show: "#f43f5e",
};

export function DashboardPage() {
    const { coach } = useAuth();
    const { t, locale } = useI18n();
    const [range, setRange] = useState<DashboardRange>("week");

    const { data, isPending, isError, error, refetch } = useCoachDashboard(range);

    const currency = useMemo(
        () =>
            new Intl.NumberFormat(locale, {
                style: "currency",
                currency: data?.payroll.currency ?? "IDR",
                maximumFractionDigits: 0,
            }),
        [locale, data?.payroll.currency],
    );

    const trendData: TrendPoint[] = data?.trend ?? [];

    const attendanceSlices: ChartSlice[] = useMemo(
        () =>
            (data?.attendance ?? []).map((slice) => ({
                id: slice.status,
                label: statusLabel(slice.status),
                value: slice.count,
                color: ATTENDANCE_COLORS[slice.status],
            })),
        [data],
    );

    const outletSlices: ChartSlice[] = useMemo(
        () =>
            (data?.outlets ?? []).map((outlet) => ({
                id: outlet.id,
                label: outlet.id === "unspecified" ? t("dashboard.unspecified") : outlet.label,
                value: outlet.sessions,
            })),
        [data, t],
    );

    const sportSlices: ChartSlice[] = useMemo(
        () =>
            (data?.sports ?? []).map((sport) => ({
                id: sport.id,
                label: sport.id === "unspecified" ? t("dashboard.unspecified") : sport.label,
                value: sport.sessions,
            })),
        [data, t],
    );

    const sessionsTrend = toTrend(data?.sessions);
    const hoursTrend = toTrend(data?.teachingMinutes);

    const rangeOptions = [
        { value: "week" as const, label: t("dashboard.range.week") },
        { value: "month" as const, label: t("dashboard.range.month") },
    ];

    return (
        <div className="flex flex-col gap-6">
            <header className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
                <div>
                    <h1 className="app-text-primary text-2xl font-bold sm:text-3xl">
                        {t("dashboard.greeting", {
                            name: coach?.displayName ?? coach?.coachName ?? "Coach",
                        })}
                    </h1>
                    <p className="app-shell-text-muted mt-1 text-sm">
                        {data
                            ? `${t("dashboard.subtitle")} · ${formatDayMonth(data.period.from)} – ${formatDayMonth(data.period.to)}`
                            : t("dashboard.subtitle")}
                    </p>
                </div>

                <SegmentedControl
                    label={t("dashboard.range.label")}
                    options={rangeOptions}
                    value={range}
                    onChange={setRange}
                />
            </header>

            {isPending && (
                <Card>
                    <LoadingBlock label={t("dashboard.loading")} />
                </Card>
            )}

            {isError && (
                <Card>
                    <ErrorState
                        message={
                            error instanceof ApiError
                                ? error.message
                                : t("dashboard.loadFailed")
                        }
                        onRetry={() => void refetch()}
                    />
                </Card>
            )}

            {data && (
                <>
                    <section className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
                        <StatsCard
                            label={t("dashboard.stats.sessions")}
                            value={data.sessions.current}
                            icon={<CalendarClock className="h-5 w-5" />}
                            hint={t("dashboard.stats.vsPrevious")}
                            trend={sessionsTrend}
                        />
                        <StatsCard
                            label={t("dashboard.stats.teachingHours")}
                            value={formatDuration(data.teachingMinutes.current)}
                            icon={<Clock3 className="h-5 w-5" />}
                            hint={t("dashboard.stats.vsPrevious")}
                            trend={hoursTrend}
                        />
                        <StatsCard
                            label={t("dashboard.stats.completionRate")}
                            value={
                                data.completionRate === null
                                    ? "—"
                                    : `${data.completionRate}%`
                            }
                            icon={<TrendingUp className="h-5 w-5" />}
                            hint={t("dashboard.stats.completionHint")}
                        />
                        <StatsCard
                            label={t("dashboard.stats.rating")}
                            value={data.rating === null ? "—" : data.rating.toFixed(1)}
                            icon={<Star className="h-5 w-5" />}
                            hint={t("dashboard.stats.ratingHint")}
                        />
                    </section>

                    <section className="grid grid-cols-1 gap-4 lg:grid-cols-3">
                        <Card className="lg:col-span-2">
                            <CardHeader
                                title={t("dashboard.charts.trend.title")}
                                description={t("dashboard.charts.trend.subtitle")}
                                icon={<TrendingUp className="h-5 w-5" />}
                            />
                            <CardContent>
                                <TrendBarChart
                                    data={trendData}
                                    formatXLabel={formatWeekday}
                                    formatDuration={formatDuration}
                                    sessionsLabel={t("dashboard.stats.sessions")}
                                    durationLabel={t("dashboard.stats.teachingHours")}
                                />
                            </CardContent>
                        </Card>

                        <Card>
                            <CardHeader
                                title={t("dashboard.charts.attendance.title")}
                                description={t("dashboard.charts.attendance.subtitle")}
                            />
                            <CardContent>
                                {attendanceSlices.length === 0 ? (
                                    <EmptyState
                                        title={t("dashboard.empty.title")}
                                        description={t("dashboard.empty.description")}
                                    />
                                ) : (
                                    <BreakdownDonutChart
                                        data={attendanceSlices}
                                        centerValue={String(data.sessions.current)}
                                        centerLabel={t("dashboard.stats.sessions")}
                                    />
                                )}
                            </CardContent>
                        </Card>
                    </section>

                    <section className="grid grid-cols-1 gap-4 lg:grid-cols-3">
                        <Card>
                            <CardHeader title={t("dashboard.charts.outlets.title")} />
                            <CardContent>
                                {outletSlices.length === 0 ? (
                                    <EmptyState title={t("dashboard.empty.title")} />
                                ) : (
                                    <BreakdownDonutChart
                                        data={outletSlices}
                                        centerValue={String(data.sessions.current)}
                                        centerLabel={t("dashboard.stats.sessions")}
                                    />
                                )}
                            </CardContent>
                        </Card>

                        <Card>
                            <CardHeader title={t("dashboard.charts.sports.title")} />
                            <CardContent>
                                {sportSlices.length === 0 ? (
                                    <EmptyState title={t("dashboard.empty.title")} />
                                ) : (
                                    <BreakdownDonutChart
                                        data={sportSlices}
                                        centerValue={String(data.sessions.current)}
                                        centerLabel={t("dashboard.stats.sessions")}
                                    />
                                )}
                            </CardContent>
                        </Card>

                        <Card>
                            <CardHeader
                                title={t("dashboard.payroll.title")}
                                description={t("dashboard.payroll.subtitle")}
                                icon={<Wallet className="h-5 w-5" />}
                            />
                            <CardContent className="flex flex-col gap-4">
                                <div>
                                    <p className="app-text-primary text-3xl font-bold">
                                        {data.payroll.amount === null
                                            ? "—"
                                            : currency.format(data.payroll.amount)}
                                    </p>
                                    <p className="app-text-soft mt-1 text-xs">
                                        {t("dashboard.payroll.disclaimer")}
                                    </p>
                                </div>

                                {data.payroll.note && (
                                    <p className="app-shell-text-muted text-sm">
                                        {t(`dashboard.payroll.note.${data.payroll.note}`)}
                                    </p>
                                )}

                                <dl className="app-content-subtle grid grid-cols-2 gap-3 rounded-xl p-4 text-sm">
                                    <div>
                                        <dt className="app-text-soft text-xs">
                                            {t("dashboard.payroll.eligibleSessions")}
                                        </dt>
                                        <dd className="app-text-primary font-semibold">
                                            {data.payroll.eligibleSessions}
                                        </dd>
                                    </div>
                                    <div>
                                        <dt className="app-text-soft text-xs">
                                            {t("dashboard.payroll.eligibleHours")}
                                        </dt>
                                        <dd className="app-text-primary font-semibold">
                                            {formatDuration(data.payroll.eligibleMinutes)}
                                        </dd>
                                    </div>
                                    <div className="col-span-2">
                                        <dt className="app-text-soft text-xs">
                                            {t("dashboard.payroll.basis")}
                                        </dt>
                                        <dd className="app-text-primary font-semibold">
                                            {data.payroll.basis ?? "—"}
                                        </dd>
                                    </div>
                                </dl>
                            </CardContent>
                        </Card>
                    </section>

                    <Card>
                        <CardHeader
                            title={t("dashboard.upcoming.title")}
                            description={t("dashboard.upcoming.subtitle")}
                            icon={<CalendarClock className="h-5 w-5" />}
                            action={
                                <Link
                                    to="/assignments"
                                    className="app-text-primary text-sm font-semibold underline"
                                >
                                    {t("dashboard.upcoming.viewAll")}
                                </Link>
                            }
                        />
                        <CardContent noPadding>
                            {data.upcoming.length === 0 ? (
                                <EmptyState
                                    title={t("dashboard.upcoming.empty")}
                                    description={t("dashboard.upcoming.emptyDescription")}
                                />
                            ) : (
                                <ul className="divide-y app-shell-border">
                                    {data.upcoming.map((assignment) => (
                                        <li
                                            key={assignment.id}
                                            className="flex flex-col gap-2 px-5 py-4 sm:flex-row sm:items-center sm:justify-between"
                                        >
                                            <div className="min-w-0">
                                                <p className="app-text-primary truncate font-semibold">
                                                    {assignment.sourceName ??
                                                        assignment.sport?.name ??
                                                        assignment.outlet?.name ??
                                                        t("dashboard.unspecified")}
                                                </p>
                                                <p className="app-shell-text-muted truncate text-sm">
                                                    {formatDayMonth(assignment.assignmentDate)} ·{" "}
                                                    {formatTimeRange(
                                                        assignment.startTime,
                                                        assignment.endTime,
                                                    )}{" "}
                                                    · {assignment.outlet?.name}
                                                </p>
                                            </div>
                                            <Badge tone={statusTone(assignment.status)}>
                                                {statusLabel(assignment.status)}
                                            </Badge>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </CardContent>
                    </Card>
                </>
            )}
        </div>
    );
}

/** Ubah `DashboardDelta` jadi badge tren untuk `StatsCard`. */
function toTrend(value?: {
    delta: number;
    deltaPct: number | null;
}): { value: string; direction: "up" | "down" | "flat" } {
    if (!value) return { value: "—", direction: "flat" };

    const direction =
        value.delta > 0 ? "up" : value.delta < 0 ? "down" : "flat";

    if (value.deltaPct === null) {
        return {
            value: value.delta === 0 ? "0" : `${value.delta > 0 ? "+" : ""}${value.delta}`,
            direction,
        };
    }

    return {
        value: `${value.deltaPct > 0 ? "+" : ""}${value.deltaPct}%`,
        direction,
    };
}
